import { ReactNode } from "react";

import type { OctantLengthOverrides, OctantZone } from "constants/octantZone.constants";
import { IScaleBy } from "constants/visualization.constants";

import { IGroupByMap } from "models";
import { IpdbDisplaySettings } from "models/ipdbDisplaySettings";

export type State = {
  xda: {
    settings: XDASettingsT;
  };
};

export type VisProviderT = {
  children: ReactNode;
  initialState?: State;
};

export const IpdbColors = ["rainbow", "jet", "viridis", "portland"] as const;
export const IpdbNumOfColorsForPalettes = [9, 6, 9, 5] as const;
export type IpdbColor = (typeof IpdbColors)[number];
export type IpdbNumOfColorsForPalette = (typeof IpdbNumOfColorsForPalettes)[number];

export class IpdbBin {
  lessThan?: number;
  binSize?: number;
  greaterThan?: number;
  isLocked?: boolean;

  constructor(lessThan?: number, binSize?: number, greaterThan?: number) {
    this.lessThan = lessThan;
    this.binSize = binSize;
    this.greaterThan = greaterThan;
  }
}

export enum XDA_RADIUS_TYPES {
  Gross = "Gross",
  SweetSpot = "Sweet Spot",
  LandingZone = "Landing Zone",
  Custom = "Custom"
}

// `showIpdb` (show 3D geo model) and `reverseColor` are sourced from the
// shared, drift-free `IpdbDisplaySettings` intersection (also composed by
// `IpdbState` in components/map/contexts/ipdbStore.ts) instead of being
// redeclared here.
export type XDASettingsT = IpdbDisplaySettings & {
  activeXdaPlay?: string;
  bin?: IpdbBin;
  completionLength: number;
  dataFields: IGroupByMap[];
  downHeight: number;
  hangWellsToTop: boolean;
  isUsingResourcePlayDefault: boolean;
  ipdbColor: IpdbColor;
  ipdbField: string;
  ipdbUnit: string;
  ipdbSource: string;
  ipdbTitleSize: number;
  ipdbFontSize: number;
  ipdbOpacity: number;
  leftWidth: number;
  rightWidth: number;
  scaleByOption: IScaleBy;
  scaleByValue: number;
  selectedPlays: string[];
  showCompletion: boolean;
  showData: boolean;
  showGrid: boolean;
  showIpdbLegend: boolean;
  showMeasurement: boolean;
  showTooltip: boolean;
  showXdaValueInfo: boolean;
  showRelativeDepth: boolean;
  showTVDSSDepth: boolean;
  upHeight: number;
  topsModelSource: string[];
  showAllTops: boolean;
  showOverlap: boolean;
  // EVA-185: draw the surveyed, non-producing wells crossing the line (every
  // status except abandoned) as dots. Off by default; rides on the /line request.
  showDucWells: boolean;
  selectedRadius: string;
  widthScaled: number;
  resetZuiInstance: boolean;
  includeZero: boolean;
  // ── Spacing Class controls (EVA-163) ──────────────────────────────────────
  // The Model Engine octant parameters, made per-widget editable. All four are
  // consumed ONLY through `resolveOctantThresholds`, which layers them over the
  // unit-native defaults and validates them — never read them raw.
  //
  // Which octant zones the spacing overlay labels and the legend lists. EMPTY
  // means every zone (see `resolveVisibleZones`), which is also what a widget
  // saved before EVA-163 resolves to.
  // OPTIONAL, and the `?` is load-bearing: `createXdaSettingsStore` seeds a
  // saved dashboard VERBATIM rather than merging it with factory defaults, so
  // every widget saved before EVA-163 carries none of these four. Typing them
  // as required told call sites they could compare the raw field against a
  // default, which is false on exactly those dashboards — read them through
  // `resolveOctantThresholds` / `resolveVisibleZones`, which handle absence.
  spacingClassFilter?: OctantZone[];
  // Band sizes, keyed by the unit system they were typed in and never
  // converted between systems — see the note on `OctantLengthOverrides`.
  octantLengthOverrides?: OctantLengthOverrides;
  // Cutoff angles in degrees. Unit-free, so unlike the bands there is one
  // value per widget rather than one per unit system.
  octantHorCutoffDeg?: number;
  octantVertCutoffDeg?: number;
};

// `TypeLogState`/`TypeLogAction` (the reducer-based TypeLogContext shape) were
// removed — see `typeLogStore.ts` (client selection) and
// `useTypeLogWellsQuery.ts`/`useTypeLogDocumentQuery.ts` (React Query,
// backend-sourced `wells`/`document`).

// `WellDetailsAction`/`WellDetailsState`/`WellDetailsDispatch`/
// `WellDetailsProviderT` (the reducer-based WellDetailsContext shape) were
// removed — see `wellDetailsStore.ts` (client selection: `currentGroup`) and
// `useWellDetailGroupQuery.ts` (React Query, backend-sourced `wellDetails`,
// including the `[0]` PDF-blob slot merge convention).

export type Percentile = {
  p10: number;
  p90: number;
};
